/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  ApolloError,
  Badge,
  Button,
  LoadingIndicator,
  ModalForm,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useShowMessage,
} from '../../core-ui';
import { confirm } from '../App/AppDialog';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';
import { validateUser } from '../../support/safeValidators';
import { useAuthState } from '../AuthState';
import { UserPermissionsInput } from '../../types/__generated__/globalTypes';
import { toEnumObject } from '../../support/toEnumObject';

import { UserEditForm } from './UserEditForm';
import { DELETE_USER, GET_ACCOUNT_USERS, UPDATE_USER } from './graphql';
import {
  GetAccountUsers,
  GetAccountUsersVariables,
  GetAccountUsers_account_users as User,
} from './__generated__/GetAccountUsers';
import { UpdateUser, UpdateUserVariables } from './__generated__/UpdateUser';
import { DeleteUser, DeleteUserVariables } from './__generated__/DeleteUser';

type Props = {
  account: { id: string };
};

export default function UserListView(props: Props) {
  const { account } = props;
  const auth = useAuthState();
  const myUserId = auth.user.id;

  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { error, data } = useQuery<GetAccountUsers, GetAccountUsersVariables>(
    GET_ACCOUNT_USERS,
    {
      variables: { accountId: account.id },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [updateUser, { loading: isUpdatingUser }] = useMutation<
    UpdateUser,
    UpdateUserVariables
  >(UPDATE_USER, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage('success', t('User saved successfully'));
      setSelectedUser(null);
    },
    refetchQueries: [GET_ACCOUNT_USERS],
  });

  const [deleteUser] = useMutation<DeleteUser, DeleteUserVariables>(
    DELETE_USER,
    {
      onError: showApolloError,
      onCompleted: () => {
        showMessage('success', t('User deleted successfully'));
      },
      refetchQueries: [GET_ACCOUNT_USERS],
    },
  );

  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.account) {
    // TODO: Make a Not Found component
    return <Text>{t('Not Found')}</Text>;
  }

  const { users } = data.account;

  return (
    <>
      {users.length === 0 ? (
        <Text>{t('No users to show.')}</Text>
      ) : (
        <Table w="full">
          <Thead>
            <Tr>
              <Th>{t('Full Name')}</Th>
              <Th>{t('Username')}</Th>
              <Th textAlign="center">{t('Actions')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td width="40%">
                  {user.name}
                  {user.permissions?.users === 'WRITE' && (
                    <Badge mx={2} colorScheme="teal">
                      {t('Market Manager')}
                    </Badge>
                  )}
                </Td>
                <Td width="40%">{user.username}</Td>
                <Td width="20%">
                  <Stack direction="row" justifyContent="center">
                    <Button
                      colorScheme="grey"
                      variant="link"
                      onClick={() => {
                        setSelectedUser(user);
                      }}
                    >
                      {t('Edit')}
                    </Button>
                    <Button
                      colorScheme="grey"
                      variant="link"
                      onClick={async () => {
                        if (user.id === myUserId) {
                          showMessage(
                            'error',
                            t("You cannot delete the user you're logged in as"),
                          );
                          return;
                        }
                        const proceed = await confirm(
                          t('Delete User?'),
                          t(
                            'Are you sure you want to permanently delete this user?',
                          ),
                        );
                        if (!proceed) {
                          return;
                        }
                        deleteUser({ variables: { id: user.id } });
                      }}
                    >
                      {t('Delete')}
                    </Button>
                  </Stack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      <ModalForm
        title={t('Edit User')}
        isOpen={selectedUser != null}
        submitButtonText={t('Save')}
        isSubmitting={isUpdatingUser}
        onClose={() => setSelectedUser(null)}
      >
        {selectedUser ? (
          <UserEditForm
            user={selectedUser}
            onSave={(user) => {
              const { isValid, reason } = validateUser(user, {
                allowEmptyPassword: true,
              });
              if (!isValid) {
                showMessage('error', reason);
                return;
              }
              if (!user.password) {
                delete user.password;
              }
              updateUser({
                variables: {
                  id: selectedUser.id,
                  updates: {
                    name: user.name,
                    email: user.email,
                    username: user.username,
                    password: user.password,
                    permissions: toEnumObject<UserPermissionsInput>(
                      user.permissions,
                    ),
                    suspended: user.suspended,
                  },
                },
              });
            }}
            passwordPlaceholder={t('Leave blank to keep password unchanged')}
          />
        ) : null}
      </ModalForm>
    </>
  );
}
